import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  Mic2,
  CalendarDays,
  CalendarClock,
  MousePointerClick,
  FileText,
  Sparkles,
  RotateCcw,
  Repeat2,
  BarChart3,
  Settings,
  Menu,
  X,
  LogOut,
} from "lucide-react";
import { fetchMe, logout, type MeResponse } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { apiErrorMessage } from "../../lib/errors";
import { SystemStatusBadge } from "../../components/system-status-badge";
import { AddEmailModal } from "../../components/add-email-modal";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutGrid;
  end?: boolean;
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

const NAV: NavSection[] = [
  {
    items: [
      { to: "/app", label: "Projects", icon: LayoutGrid, end: true },
      { to: "/app/voices", label: "Voices", icon: Mic2 },
      { to: "/app/drafts", label: "Drafts", icon: FileText },
      { to: "/app/hooks", label: "Hooks", icon: Sparkles },
    ],
  },
  {
    label: "Publishing",
    items: [
      { to: "/app/queue", label: "Queue", icon: CalendarDays },
      { to: "/app/schedule", label: "Schedule", icon: CalendarClock },
      { to: "/app/auto-plug", label: "Auto-plug", icon: MousePointerClick },
    ],
  },
  {
    label: "Recycle",
    items: [
      { to: "/app/evergreen", label: "Evergreen", icon: RotateCcw },
      { to: "/app/auto-retweet", label: "Retweet", icon: Repeat2 },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/app/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/app/settings", label: "Settings", icon: Settings },
    ],
  },
];

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    fetchMe()
      .then((res) => {
        if (!res) {
          navigate("/login", { replace: true });
          return;
        }
        setMe(res);
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err));
      })
      .finally(() => setLoading(false));
  }, [navigate, toast]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  async function handleLogout() {
    try {
      await logout();
    } catch (err) {
      toast.error(apiErrorMessage(err));
      return;
    }
    navigate("/login", { replace: true });
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink-500">
        Loading…
      </div>
    );
  }
  if (!me) return null;

  return (
    <div className="min-h-screen lg:flex">
      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-ink-200/80 bg-white/85 px-4 py-3 backdrop-blur-md lg:hidden">
        <Link to="/app" className="flex items-center gap-2 no-underline">
          <BrandMark />
          <span className="text-base font-semibold text-ink-900">BrandEcho</span>
        </Link>
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-ink-200 text-ink-700"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 transform border-r border-ink-200/80 bg-white transition-transform duration-200 lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Brand */}
          <div className="flex h-16 items-center justify-between border-b border-ink-200/60 px-5">
            <Link to="/app" className="flex items-center gap-2.5 no-underline">
              <BrandMark />
              <span className="text-base font-semibold text-ink-900">
                BrandEcho
              </span>
            </Link>
            <button
              className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900 lg:hidden"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto px-3 py-4">
            {NAV.map((section, idx) => (
              <div key={idx}>
                {section.label ? (
                  <p className="sidebar-section-label">{section.label}</p>
                ) : null}
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          `group sidebar-link ${isActive ? "sidebar-link-active" : ""}`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <Icon
                              className={`sidebar-icon ${
                                isActive ? "sidebar-icon-active" : ""
                              }`}
                            />
                            <span>{item.label}</span>
                          </>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="border-t border-ink-200/60 p-3">
            <CreditsCard me={me} />
            <SystemStatusBadge />
            <button
              onClick={handleLogout}
              className="mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-ink-600 transition-colors hover:bg-ink-100 hover:text-ink-900"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Backdrop */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      {/* Main content */}
      <main className="flex-1 lg:overflow-y-auto">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-10">
          <Outlet
            context={{
              me,
              refreshMe: () => fetchMe().then((r) => r && setMe(r)),
            }}
          />
        </div>
      </main>

      <AddEmailModal
        open={Boolean(me?.user?.email_is_placeholder)}
        onSaved={() => fetchMe().then((r) => r && setMe(r))}
      />
    </div>
  );
}

function BrandMark() {
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-gradient text-white shadow-sm">
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 8 L 8 3 L 13 8 L 8 13 Z"
          fill="white"
          fillOpacity="0.95"
        />
        <circle cx="8" cy="8" r="1.6" fill="rgba(91,108,243,1)" />
      </svg>
    </div>
  );
}

function CreditsCard({ me }: { me: MeResponse }) {
  const { remaining, quota, plan } = me.credits;
  const ratio = quota > 0 ? Math.max(0, Math.min(1, remaining / quota)) : 0;
  const empty = remaining === 0;
  const low = remaining <= Math.max(1, Math.floor(quota * 0.2));
  const tone = empty
    ? "text-red-600"
    : low
      ? "text-amber-600"
      : "text-ink-700";
  const barTone = empty
    ? "bg-red-500"
    : low
      ? "bg-amber-500"
      : "bg-brand-gradient";
  return (
    <Link
      to="/pricing"
      className="block rounded-lg border border-ink-200/70 bg-white p-3 no-underline transition-colors hover:border-ink-300"
      title={`${remaining} of ${quota} generations left this month`}
    >
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-2xs font-medium uppercase tracking-wide text-ink-500">
          {plan}
        </span>
        <span className={`text-xs font-semibold ${tone}`}>
          {remaining}/{quota}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-ink-100">
        <div
          className={`h-full rounded-full transition-all ${barTone}`}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
      <p className="mt-1.5 text-2xs text-ink-500">
        Resets {new Date(me.credits.resets_at).toLocaleDateString()}
      </p>
    </Link>
  );
}
